// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useLazyQuery, useMutation } from '@apollo/client';
import { clientLogger } from '@seedplanner/logger';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '../../lib/base';
import type {
  EditTaskNotesDataQuery,
  EditTaskNotesDataQueryVariables,
  EditTaskNotesMutation,
  EditTaskNotesMutationVariables,
} from '../../types/__generated__/graphql';
import { ExitDialogButton } from '../common/button';
import { ErrorView } from '../common/error';
import { PageLoading } from '../common/loading';

const EDIT_TASK_NOTES_MUTATION = gql`
  mutation EditTaskNotes($task_ids: [Int!]!, $new_notes: String!) {
    editTaskNotes(input: { task_ids: $task_ids, new_notes: $new_notes }) {
      updated_tasks {
        id
        notes
        updated_at
      }
    }
  }
`;

// TODO: maybe want to use one or more task IDs to get task groups
// Instead of individual tasks
const EDIT_TASK_NOTES_DATA_QUERY = gql`
  query EditTaskNotesData($ids: String!) {
    tasks(
      filters: [{ name: "task.id", operator: "in", value: $ids }]
    ) {
      edges {
        node {
          id
          notes
        }
      }
    }
  }
`;

interface Props {
  readonly taskIds: readonly number[];
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

export default function EditTaskNotesDialog({ isOpen, onClose, onSuccess, taskIds }: Props) {
  const [getTaskNotes, { loading: taskDataLoading }] = useLazyQuery<
    EditTaskNotesDataQuery,
    EditTaskNotesDataQueryVariables
  >(EDIT_TASK_NOTES_DATA_QUERY, {
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });
  const [editTaskNotes, { loading: editTaskNotesLoading, error: editTaskNotesError }] = useMutation<
    EditTaskNotesMutation,
    EditTaskNotesMutationVariables
  >(EDIT_TASK_NOTES_MUTATION);

  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (isOpen && !taskDataLoading) {
      const timeOutId = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => {
        clearTimeout(timeOutId);
      };
    }
    return () => {};
  }, [isOpen, taskDataLoading]);
  React.useEffect(() => {
    if (isOpen) {
      if (taskIds.length === 0) {
        return;
      }
      void getTaskNotes({
        variables: {
          ids: taskIds.map((t) => t).join(','),
        },
        onCompleted: (dataIn) => {
          if (dataIn?.tasks != null) {
            // TODO: this is hacky since not all tasks might not be in sync
            // It could also be getting tasks across multiple "groups"
            const notes = dataIn.tasks.edges.map(({ node }) => node.notes).find((n) => n != null && n !== '') ?? '';
            setNotes(notes);
          }
        },
        onError: (errorIn) => {
          enqueueSnackbar(`Error getting task data: ${errorIn.message}`, {
            variant: 'error',
            autoHideDuration: 10000,
          });
        },
      });
    }
  }, [isOpen, getTaskNotes, taskIds]);

  const [notes, setNotes] = React.useState('');
  const onNotesChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNotes(e.target.value);
  };

  const onSubmit = (_e?: React.MouseEvent | React.TouchEvent) => {
    void editTaskNotes({
      variables: {
        task_ids: taskIds.map((t) => t),
        new_notes: notes ?? '',
      },
      onCompleted: (data) => {
        if (data !== undefined && data.editTaskNotes != null) {
          const taskDataIn = data.editTaskNotes;
          enqueueSnackbar('Updated task notes', { variant: 'success' });
          setNotes(taskDataIn.updated_tasks[0]?.notes ?? '');
          onSuccess();
        } else {
          enqueueSnackbar('Something went wrong!', {
            variant: 'error',
            autoHideDuration: 10000,
          });
        }
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
        clientLogger.error({ title: 'new_task_error', message: errorIn.message });
      },
    });
  };

  const disableFields = taskDataLoading || editTaskNotesLoading;
  const isSubmitDisabled = taskDataLoading || editTaskNotesLoading;

  const form = (
    <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={2}>
      <TextField
        inputRef={inputRef}
        aria-describedby="task-notes"
        id="task-notes"
        label="Notes"
        value={notes}
        onChange={onNotesChange}
        // onEnter={onEnter}
        disabled={disableFields}
      />
    </Box>
  );

  const dialogContent = editTaskNotesLoading ? (
    <PageLoading />
  ) : editTaskNotesError ? (
    <ErrorView error={editTaskNotesError} />
  ) : (
    form
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Edit Task Notes</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 1 }}>{dialogContent}</DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button sx={{ width: { xs: '100%', md: '50%' } }} variant="outlined" onClick={onClose} disabled={disableFields}>
          Cancel
        </Button>
        <Button
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
